import { UserOutlined } from '@ant-design/icons';
import { App,Form, Image, Input } from 'antd';
import { useDeleteSponserEventImage } from 'features/events/use-cases/delete-sponser-image';
import { FormSectionsSeparator } from 'features/users/components/modals/form-sections-separator';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SponserFormField } from './types';

type Props = {
    fileData: File[];
    setFileData: React.Dispatch<React.SetStateAction<File[]>>;
    sponserData: any;
};

export const AddSponserForm = ({
    fileData,
    setFileData,
    sponserData
}: Props) => {

    const { t } = useTranslation();
    const { message } = App.useApp();

    const { mutate: deleteSponserImage, isLoading } =
        useDeleteSponserEventImage();

    const [images, setImages] = useState<any[]>([]);

    // ================= RESET STATE ON OPEN =================
    useEffect(() => {
        if (sponserData?.image_url) {
            setImages(sponserData.image_url);
        } else {
            setImages([]);
        }
    }, [sponserData?.id]); // ✅ important fix

    // ================= FILE UPLOAD =================
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const fileArray = Array.from(files);
        setFileData((prev) => [...prev, ...fileArray]);
    };

    // ================= DELETE IMAGE =================
    const handleRemoveImage = (img: any) => {
        deleteSponserImage(
            {
                mappingId: img.Id,
                sponsorId: sponserData?.id,
            },
            {
                onSuccess: () => {
                setImages((prev) => prev.filter((item) => item.Id !== img.Id));
                    message.success('Image deleted successfully');
                },
            }
        );
    };

    // ================= REMOVE FILE =================
    const handleRemoveFile = (index: number) => {
        setFileData((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <FormSectionsSeparator title="">

            {/* NAME */}
            <Form.Item
                label="Name"
                name={SponserFormField.Name}
                rules={[
                    {
                        required: true,
                        whitespace: true,
                        message: t('validateMessages.custom.nomsg')
                    }
                ]}
            >
                <Input placeholder="Enter Name" prefix={<UserOutlined />} />
            </Form.Item>

            {/* LINK */}
            <Form.Item label="Link" name={SponserFormField.Link}>
                <Input placeholder="Enter Link" />
            </Form.Item>

            {/* CONTACT */}
            <Form.Item label="Contact Number" name={SponserFormField.ContactNumber}>
                <Input placeholder="Enter Contact Number" />
            </Form.Item>

            {/* ADDRESS */}
            <Form.Item label="Address" name={SponserFormField.Address}>
                <Input placeholder="Enter Address" />
            </Form.Item>

            {/* EMAIL */}
            <Form.Item label="Email" name={SponserFormField.Email}>
                <Input placeholder="Enter Email" />
            </Form.Item>

            {/* ================= EXISTING IMAGES ================= */}
            {images.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <label>Existing Images</label>

                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {images.map((img: any) => (
                            <div
                                key={img.Id}
                                style={{
                                    position: 'relative',
                                    width: 100,
                                    height: 100,
                                    opacity: isLoading ? 0.6 : 1,
                                    pointerEvents: isLoading ? 'none' : 'auto'
                                }}
                            >
                                <Image
                                    src={img.Image_Url}
                                    width={100}
                                    height={100}
                                    style={{
                                        objectFit: 'cover',
                                        borderRadius: 6
                                    }}
                                />

                                {/* DELETE */}
                                <span
                                    onClick={() => handleRemoveImage(img)}
                                    style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        width: 18,
                                        height: 18,
                                        background: 'red',
                                        color: '#fff',
                                        borderRadius: '50%',
                                        fontSize: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ================= NEW IMAGES ================= */}
            {fileData.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <label>New Images</label>

                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {fileData.map((file, index) => (
                            <div
                                key={index}
                                style={{
                                    position: 'relative',
                                    width: 100,
                                    height: 100
                                }}
                            >
                                <Image
                                    src={URL.createObjectURL(file)}
                                    width={100}
                                    height={100}
                                    style={{
                                        objectFit: 'cover',
                                        borderRadius: 6
                                    }}
                                />

                                {/* REMOVE */}
                                <span
                                    onClick={() => handleRemoveFile(index)}
                                    style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        width: 18,
                                        height: 18,
                                        background: 'red',
                                        color: '#fff',
                                        borderRadius: '50%',
                                        fontSize: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ================= UPLOAD ================= */}
            <Form.Item
                label="Image"
                name={SponserFormField.File}
                rules={[
                    {
                        required:
                            !sponserData?.id &&
                            fileData.length === 0 &&
                            images.length === 0,
                        message: t('validateMessages.custom.nomsg'),
                    },
                ]}
            >
                <Input
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg"
                    onChange={handleFileChange}
                />
            </Form.Item>

        </FormSectionsSeparator>
    );
};